import React, { useState } from "react";
import { Play, Heart, MessageCircle, Video, Music2, MoreHorizontal, Share2, ListPlus, BadgeCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { fileStreamUrl } from "../lib/api";
import { usePlayer } from "../context/PlayerContext";
import ShareModal from "./ShareModal";
import AddToPlaylistModal from "./AddToPlaylistModal";
import { ProviderBadge } from "./EmbedPlayer";

export const TrackCard = ({ track, onPlay, list = [], playlistId = null }) => {
  const { playTrack, currentTrack, isPlaying } = usePlayer();
  const [showShare, setShowShare] = useState(false);
  const [showAddPlaylist, setShowAddPlaylist] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const isCurrent = currentTrack?.track_id === track.track_id;

  const trackHref = playlistId ? `/track/${track.track_id}?playlist=${playlistId}` : `/track/${track.track_id}`;

  const handlePlay = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Vidéos externes (YouTube, TikTok...) ou locales : naviguer vers la page détail
    if (track.track_type === "video" || track.external_embed) {
      window.location.href = trackHref;
      return;
    }
    playTrack(track, list);
    onPlay?.(track);
  };

  const stop = (e) => { e.preventDefault(); e.stopPropagation(); };

  const coverSrc = track.cover_url || (track.cover_path && !/^https?:/i.test(track.cover_path) ? fileStreamUrl(track.cover_path) : track.cover_path);

  return (
    <>
      <Link
        to={trackHref}
        className="group flex flex-col gap-3 p-2.5 rounded-2xl bg-transparent hover:bg-white/[0.03] border border-transparent hover:border-white/10 transition-all duration-300 hover:-translate-y-1"
        data-testid={`track-card-${track.track_id}`}
      >
        <div className="relative aspect-video rounded-xl overflow-hidden bg-brand-elevated border border-white/5">
          {coverSrc ? (
            <img
              src={coverSrc}
              alt={track.title}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-brand-red/40 via-brand-orange/30 to-brand-yellow/30 flex items-center justify-center">
              {track.track_type === "video" ? <Video size={36} className="text-white/70" /> : <Music2 size={36} className="text-white/70" />}
            </div>
          )}

          {track.external_provider && (
            <div className="absolute top-2 left-2 z-10">
              <ProviderBadge provider={track.external_provider} />
            </div>
          )}

          {/* Overlay + bouton Play centré au hover */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center backdrop-blur-[1px]">
            <button
              onClick={handlePlay}
              className="w-14 h-14 rounded-full bg-brand-red text-white flex items-center justify-center shadow-[0_0_30px_rgba(227,27,35,0.55)] transform scale-75 group-hover:scale-100 transition-transform duration-300 hover:bg-[#C1171D]"
              data-testid={`play-track-${track.track_id}`}
              aria-label="Lire"
            >
              <Play size={20} fill="currentColor" className="ml-0.5" />
            </button>
          </div>

          <div className="absolute top-2 right-2 flex flex-col gap-1">
            <button
              onClick={(e) => { stop(e); setMenuOpen(!menuOpen); }}
              className="w-8 h-8 rounded-full bg-black/60 backdrop-blur text-white opacity-0 group-hover:opacity-100 flex items-center justify-center hover:bg-black/80 transition-opacity"
              data-testid={`menu-track-${track.track_id}`}
            >
              <MoreHorizontal size={14} />
            </button>
            {menuOpen && (
              <div onClick={stop} className="absolute top-10 right-0 min-w-[180px] bg-brand-surface border border-white/10 rounded-xl p-1 shadow-xl z-10" data-testid={`menu-open-${track.track_id}`}>
                <button onClick={(e) => { stop(e); setMenuOpen(false); setShowAddPlaylist(true); }} className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg hover:bg-white/5" data-testid={`menu-add-playlist-${track.track_id}`}>
                  <ListPlus size={14} /> Ajouter à une playlist
                </button>
                <button onClick={(e) => { stop(e); setMenuOpen(false); setShowShare(true); }} className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg hover:bg-white/5" data-testid={`menu-share-${track.track_id}`}>
                  <Share2 size={14} /> Partager
                </button>
              </div>
            )}
          </div>

          {track.track_type === "video" && (
            <span className="absolute bottom-2 left-2 px-2 py-0.5 rounded-md bg-black/70 backdrop-blur text-[10px] font-bold uppercase tracking-wider flex items-center gap-1">
              <Video size={10} /> Vidéo
            </span>
          )}
          {track.is_featured && (
            <span className="absolute bottom-2 right-2 px-2 py-0.5 rounded-md bg-brand-yellow/95 text-brand-dark text-[10px] font-black uppercase tracking-wider inline-flex items-center gap-1">
              <BadgeCheck size={10} /> Vedette
            </span>
          )}
        </div>

        <div className="min-w-0 px-1 pb-1">
          <h3 className="font-semibold text-sm md:text-base truncate group-hover:text-brand-red transition-colors">
            {track.title}
          </h3>
          <p className="text-xs md:text-sm text-white/55 truncate mt-0.5">{track.artist_name}</p>
          <div className="flex items-center gap-3 mt-2 text-[11px] text-white/40">
            <span className="flex items-center gap-1"><Play size={11} /> {track.plays}</span>
            <span className="flex items-center gap-1"><Heart size={11} /> {track.likes_count}</span>
            <span className="flex items-center gap-1"><MessageCircle size={11} /> {track.comments_count}</span>
            {track.genre && <span className="ml-auto text-brand-orange font-medium truncate max-w-[60%]">{track.genre}</span>}
          </div>
        </div>
      </Link>

      <ShareModal open={showShare} onClose={() => setShowShare(false)} title={`${track.title} — ${track.artist_name}`} path={`/track/${track.track_id}`} />
      <AddToPlaylistModal open={showAddPlaylist} onClose={() => setShowAddPlaylist(false)} trackId={track.track_id} />
    </>
  );
};

export default TrackCard;
